import { useState, useCallback } from 'react';
import DownIcon from '@common/components/icons/DownIcon';
import CreateButton from '@common/components/CreateButton';
import { FilterDropdown } from '@common/components/FilterDropdown';
import { hasValidValue, createEmptyFilter } from '@common/utils/filterUtils';
import Search from './Search';
import FilterChip from './FilterChip';
import * as styles from './index.module.css';

export default function Toolbar({
  onSearchChange,
  onFiltersChange,
  onSearchRemove,
  searchTerm = '',
  filters = [],
  filterTypes = [],
  filterValues = {},
  createButton,
  loadFilterOptions,
  onFilterDropdownOpen,
}) {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const toggleFiltersDropdown = () => {
    setIsFiltersOpen((prev) => !prev);
  };

  const closeFiltersDropdown = () => {
    setIsFiltersOpen(false);
  };

  const handleFilterRemove = (filterId) => {
    if (!onFiltersChange) return;
    const newFilters = filters.filter((filter) => filter.id !== filterId);
    if (newFilters.length === 0) {
      newFilters.push(createEmptyFilter());
    }
    onFiltersChange(newFilters);
  };

  const handleClearAllFilters = useCallback(() => {
    if (onFiltersChange) {
      const resetFilters = [createEmptyFilter()];
      onFiltersChange(resetFilters);
    }
    if (onSearchRemove) {
      onSearchRemove();
    }
  }, [onFiltersChange, onSearchRemove]);

  const hasActiveFilters =
    searchTerm ||
    filters.some((filter) => filter.type && hasValidValue(filter.value));

  return (
    <div className={styles['toolbar-outer']}>
      <div className={styles['toolbar-container']}>
        <div className={styles['toolbar-left']}>
          <Search onSearchChange={onSearchChange} searchTerm={searchTerm} />
          {filters.length > 0 && (
            <div className={styles['filter-wrapper']}>
              <button
                onClick={toggleFiltersDropdown}
                className={styles['filter-button']}
                type="button"
              >
                <span>Filter</span>
                <DownIcon
                  className={isFiltersOpen ? styles['rotate-icon'] : ''}
                />
              </button>

              <FilterDropdown
                isOpen={isFiltersOpen}
                onClose={closeFiltersDropdown}
                filters={filters}
                onFiltersChange={onFiltersChange}
                filterTypes={filterTypes}
                filterValues={filterValues}
                loadFilterOptions={loadFilterOptions}
                onOpen={onFilterDropdownOpen}
              />
            </div>
          )}
          {/* Filter chips displayed inline after Filter button */}
          {hasActiveFilters && (
            <div className={styles['filter-chips']}>
              {searchTerm && (
                <FilterChip
                  filter={{ type: 'search', value: searchTerm }}
                  onRemove={onSearchRemove}
                  filterTypes={filterTypes}
                  filterValues={filterValues}
                />
              )}
              {filters
                .filter((filter) => filter.type && hasValidValue(filter.value))
                .map((filter) => (
                  <FilterChip
                    key={filter.id}
                    filter={filter}
                    onRemove={handleFilterRemove}
                    filterTypes={filterTypes}
                    filterValues={filterValues}
                  />
                ))}
              <button
                onClick={handleClearAllFilters}
                className={styles['clear-all-btn']}
                type="button"
              >
                Clear All
              </button>
            </div>
          )}
        </div>

        {createButton && (
          <CreateButton
            label={createButton.label}
            onClick={createButton.onClick}
          />
        )}
      </div>
    </div>
  );
}
